import React from "react";
import toast from "react-hot-toast";

import {
  getDumpYardTemplatesByType,
  showDeleteDumpYardTemplateConfirmationModal,
  setShowUpdateDumpYardModal,
} from "../dumpYardsSlice";
import { useAppSelector, useAppDispatch } from "../../../app/hooks";
import {
  DumpYardTemplate,
  DumpYardTemplateType,
  wasteTypeLabels,
} from "../../../models";

import "./DumpYardTemplatesTable.scss";

interface Props {
  type: DumpYardTemplateType;
}

export function DumpYardTemplatesTable({ type }: Props) {
  const dispatch = useAppDispatch();

  const templates = useAppSelector(
    (state) => state.dumpYards.templatesByType[type]
  );
  const templatesAPI = useAppSelector(
    (state) => state.dumpYards.getTemplatesByTypeAPI[type]
  );
  const deleteDumpYardTemplateAPI = useAppSelector(
    (state) => state.dumpYards.deleteDumpYardTemplate
  );
  const templateToRemove = useAppSelector(
    (state) => state.dumpYards.templateToRemove
  );

  const [search, setSearch] = React.useState<string>("");
  const [statusFilter, setStatusFilter] = React.useState<
    "all" | "active" | "inactive"
  >("all");

  // Lazily fetch this tab's templates via the backend ?type= filter. Runs again
  // on tab switch because the router mounts a fresh table per route.
  React.useEffect(() => {
    dispatch(getDumpYardTemplatesByType(type));
    setSearch("");
    setStatusFilter("all");
  }, [dispatch, type]);

  React.useEffect(() => {
    if (!templatesAPI.loading && templatesAPI.error) {
      toast.error("Failed to load. Please try again.");
    }
  }, [templatesAPI.loading, templatesAPI.error]);

  const isDumpYard = type === DumpYardTemplateType.DUMP_YARD;

  const filteredTemplates = React.useMemo(() => {
    const searchLower = search.toLowerCase();
    return templates.filter((d) => {
      const matchesSearch =
        search === "" ||
        d.name.toLowerCase().includes(searchLower) ||
        d.address.toLowerCase().includes(searchLower) ||
        d.zip.toLowerCase().includes(searchLower);
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" ? d.isActive : !d.isActive);
      return matchesSearch && matchesStatus;
    });
  }, [templates, search, statusFilter]);

  const handleEditTemplate = (template: DumpYardTemplate) =>
    dispatch(setShowUpdateDumpYardModal(template));
  const handleDeleteTemplate = (template: DumpYardTemplate) =>
    dispatch(showDeleteDumpYardTemplateConfirmationModal(template));

  if (templatesAPI.loading && templates.length === 0) {
    return (
      <div className="dump-yard-templates-table loading">
        <div className="loading-text">Loading...</div>
      </div>
    );
  }

  // dump_yard is the only type carrying pricing / weight / waste columns, and
  // the only type the active flag applies to (it gates optimizer routing).
  const extraColumnCount = isDumpYard ? 3 : 0;
  // name, address, zip, actions + dump-yard extras + status (dump yards only)
  const columnCount = 4 + extraColumnCount + (isDumpYard ? 1 : 0);

  return (
    <div className="dump-yard-templates-table">
      <div className="filters-row">
        <div className="filter-group search-group">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, address, or ZIP..."
            className="filter-input"
          />
        </div>
        {isDumpYard && (
          <div className="filter-group status-group">
            <select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as "all" | "active" | "inactive")
              }
              className="filter-select"
              aria-label="Filter by status"
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        )}
        <span className="count">
          {filteredTemplates.length}{" "}
          {filteredTemplates.length === 1 ? "result" : "results"}
        </span>
      </div>

      <div className="table-container">
        <div className="table-scroll-wrapper">
          <table className="templates-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Address</th>
                <th>ZIP</th>
                {isDumpYard && <th>Dumping Price</th>}
                {isDumpYard && <th>Min Dump Weight</th>}
                {isDumpYard && <th>Waste Types</th>}
                {isDumpYard && <th>Status</th>}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTemplates.length === 0 ? (
                <tr>
                  <td className="empty-row" colSpan={columnCount}>
                    {search
                      ? "No results match your search."
                      : "Nothing here yet. Add one to get started."}
                  </td>
                </tr>
              ) : (
                filteredTemplates.map((template: DumpYardTemplate) => {
                  const isDeleting =
                    deleteDumpYardTemplateAPI.loading &&
                    templateToRemove === template.id;
                  return (
                    <tr
                      key={template.id}
                      className={template.isActive ? undefined : "inactive-row"}
                    >
                      <td className="name-cell">
                        <div className="name">{template.name}</div>
                      </td>
                      <td className="address-cell">
                        <div className="address">{template.address || "-"}</div>
                      </td>
                      <td className="zip-cell">{template.zip || "-"}</td>
                      {isDumpYard && (
                        <td className="price-cell">
                          {template.pricePerTon != null
                            ? `$${template.pricePerTon.toFixed(2)}/ton`
                            : "—"}
                        </td>
                      )}
                      {isDumpYard && (
                        <td className="weight-cell">
                          {template.minDumpWeight != null
                            ? `${template.minDumpWeight} tons`
                            : "—"}
                        </td>
                      )}
                      {isDumpYard && (
                        <td className="waste-cell">
                          {template.wasteTypes.length > 0 ? (
                            <div className="waste-badges">
                              {template.wasteTypes.map((w) => (
                                <span key={w} className="waste-badge">
                                  {wasteTypeLabels[w] || w}
                                </span>
                              ))}
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                      )}
                      {isDumpYard && (
                        <td className="status-cell">
                          <span
                            className={`status-pill ${
                              template.isActive ? "active" : "inactive"
                            }`}
                          >
                            {template.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                      )}
                      <td className="actions-cell">
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-edit"
                            onClick={() => handleEditTemplate(template)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteTemplate(template)}
                            disabled={isDeleting}
                          >
                            {isDeleting ? "Deleting..." : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
